import type { CompanyData } from '@/modules/auth/helpers/CompanyStorage';
import { normalizeCompaniesById } from '@/modules/auth/utils/authUtils';
import type { Role } from './PermissionManager';

export type PermissionMap = Record<string, string[]>;

/** Menu / gate: user has any action on resource (actions.length > 0). */
export const PERMISSION_ACTION_ANY = '*';

/**
 * Frontend resource key → alternate API keys (checked before parent fallback).
 */
export const RESOURCE_ALIASES: Record<string, string[]> = {
  item: ['inventory.item'],
  customers: ['customer'],
  customer: ['customer'],
  suppliers: ['supplier'],
  supplier: ['supplier'],
  users: ['users'],
  roles: ['roles'],
  employees: ['employees'],
  orders: ['orders'],
  transactions: ['transaction'],
  transaction: ['transaction'],
  departments: ['departments'],
  companies: ['companies'],
  fixedsalary: ['employees.fixedsalary'],
  'fixed-salary': ['employees.fixedsalary'],
  allocations: ['inventory.materialrequest'],
  lead: ['customer'],
  contacts: ['customer'],
  wallet: ['transactionwallet'],
  'tasks.task': ['tasks'],
  tasks: ['tasks.task'],
  'timekeepings.timesheet': ['timesheets'],
  timesheets: ['timekeepings.timesheet'],
};

/** FE action → alternate API action names */
export const ACTION_ALIASES: Record<string, string[]> = {
  approve_transaction: ['approve'],
  approve: ['approve_transaction'],
  add: ['create'],
  create: ['add'],
  change: ['update'],
  update: ['change'],
  view: ['read'],
  read: ['view'],
};

const normalizeResourceKey = (value: string): string => value.trim().toLowerCase();

const normalizeActionKey = (value: string): string => value.trim().toLowerCase();

/**
 * App prefixes where API may grant the parent key (e.g. `tasks`) instead of `tasks.task`.
 * Do not add `timekeepings` / `timesheets` — they are separate API keys (see example.json).
 */
const PARENT_FALLBACK_PREFIXES = new Set(['tasks']);

const getParentResourceCandidates = (resource: string): string[] => {
  const trimmed = resource.trim();
  const parts = trimmed.split('.').filter(Boolean);
  if (parts.length < 2) {
    return [];
  }

  const rootPrefix = normalizeResourceKey(parts[0]);
  if (!PARENT_FALLBACK_PREFIXES.has(rootPrefix)) {
    return [];
  }

  const parents: string[] = [];
  for (let index = parts.length - 1; index > 0; index -= 1) {
    parents.push(parts.slice(0, index).join('.'));
  }

  return parents;
};

/**
 * Ordered resource keys to try: exact → aliases → parent chain (e.g. tasks.task → tasks).
 */
export const getResourceLookupCandidates = (resource: string): string[] => {
  const trimmed = resource.trim();
  if (!trimmed) {
    return [];
  }

  const normalized = normalizeResourceKey(trimmed);
  const candidates: string[] = [trimmed];

  const aliasList = RESOURCE_ALIASES[trimmed] || RESOURCE_ALIASES[normalized] || [];
  candidates.push(...aliasList);
  candidates.push(...getParentResourceCandidates(trimmed));

  const seen = new Set<string>();
  return candidates.filter((candidate) => {
    const key = normalizeResourceKey(candidate);
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const findPermissionKeyInMap = (
  permissions: PermissionMap,
  resource: string
): string | undefined => {
  if (Array.isArray(permissions[resource])) {
    return resource;
  }

  const target = normalizeResourceKey(resource);
  return Object.keys(permissions).find(
    (key) => normalizeResourceKey(key) === target
  );
};

const findPermissionKey = (
  permissions: PermissionMap,
  resource: string
): string | undefined => {
  for (const candidate of getResourceLookupCandidates(resource)) {
    const matchedKey = findPermissionKeyInMap(permissions, candidate);
    if (matchedKey) {
      return matchedKey;
    }
  }

  return undefined;
};

export const actionMatches = (
  grantedActions: string[] | undefined,
  requestedAction: string
): boolean => {
  if (!Array.isArray(grantedActions) || grantedActions.length === 0) {
    return false;
  }

  const normalizedRequested = normalizeActionKey(requestedAction);
  if (!normalizedRequested) {
    return false;
  }

  const grantedSet = new Set(grantedActions.map((action) => normalizeActionKey(action)));

  if (grantedSet.has(normalizedRequested)) {
    return true;
  }

  const aliasCandidates =
    ACTION_ALIASES[requestedAction] ||
    ACTION_ALIASES[normalizedRequested] ||
    [];

  return aliasCandidates.some((alias) => grantedSet.has(normalizeActionKey(alias)));
};

/**
 * Resolve action list for a resource from a permission map (exact → alias → parent).
 */
export function resolvePermissionActions(
  permissions: PermissionMap | undefined,
  resource: string
): string[] | undefined {
  if (!permissions) return undefined;
  const key = findPermissionKey(permissions, resource);
  return key ? permissions[key] : undefined;
}

export function hasAnyPermissionOnResource(
  permissions: PermissionMap | undefined,
  resource: string
): boolean {
  const actions = resolvePermissionActions(permissions, resource);
  return Array.isArray(actions) && actions.length > 0;
}

export function roleHasPermission(
  role: { permissions?: PermissionMap },
  resource: string,
  action: string
): boolean {
  if (action === PERMISSION_ACTION_ANY) {
    return hasAnyPermissionOnResource(role.permissions, resource);
  }

  const actions = resolvePermissionActions(role.permissions, resource);
  return actionMatches(actions, action);
}

export function mergeRolePermissions(
  roles: Array<{ permissions?: PermissionMap }>
): PermissionMap {
  const merged: PermissionMap = {};

  roles.forEach((role) => {
    if (!role.permissions) return;
    Object.entries(role.permissions).forEach(([resource, actions]) => {
      if (!Array.isArray(actions)) return;
      if (!merged[resource]) {
        merged[resource] = [];
      }
      merged[resource] = Array.from(new Set([...merged[resource], ...actions]));
    });
  });

  return merged;
}

export function hasPermissionInMap(
  permissions: PermissionMap | undefined,
  resource: string,
  action: string
): boolean {
  if (action === PERMISSION_ACTION_ANY) {
    return hasAnyPermissionOnResource(permissions, resource);
  }

  const actions = resolvePermissionActions(permissions, resource);
  return actionMatches(actions, action);
}

const normalizeRole = (role: any): Role => ({
  role_name: String(role?.role_name || ''),
  role_level: String(role?.role_level || 'company'),
  permissions:
    role?.permissions && typeof role.permissions === 'object'
      ? role.permissions
      : {},
});

export interface ParsedUserCompaniesAndRoles {
  companies: CompanyData[];
  globalRoles: Role[];
  hasGlobalRole: boolean;
}

export function parseUserCompaniesAndRolesPayload(data: unknown): ParsedUserCompaniesAndRoles {
  const payload = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const rawCompanies = payload.companies;
  const companyList = Array.isArray(rawCompanies)
    ? rawCompanies
    : Array.isArray((rawCompanies as any)?.results)
      ? (rawCompanies as any).results
      : [];

  const globalRolesRaw = payload.global_roles;
  const globalRoles = Array.isArray(globalRolesRaw)
    ? globalRolesRaw.map(normalizeRole)
    : [];

  const hasGlobalRole = Boolean(payload.has_global_role);

  return {
    companies: normalizeCompaniesById(companyList),
    globalRoles,
    hasGlobalRole,
  };
}

/**
 * After fetching full companies list, refresh selected company's roles from the same payload.
 */
export function findCompanyRolesInList(
  companies: CompanyData[],
  companyId: string
): CompanyData['roles'] {
  const id = String(companyId || '').trim();
  if (!id) return [];
  const match = companies.find((c) => String(c.company_id) === id);
  if (!match || !Array.isArray(match.roles)) return [];
  return match.roles;
}
